import { Box, Card, CardContent, SxProps, Theme, Tooltip, Typography } from '@mui/material';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { useUserDataStore } from '../../newStore/userDataStore';
import { SignerType } from './types';

interface SignerTypeCardProps {
  type: SignerType;
  title: string;
  description: string;
  imageUrl: string;
  selectedCount?: number;
  onClick: () => void;
  disabled?: boolean;
}

const customStyles: Record<string, SxProps<Theme>> = {
  signerCard: {
    cursor: 'pointer',
    border: '1px solid #e0e0e0',
    borderRadius: '20px',
    boxShadow: '0 2px 8px #00000017',
    '&:hover': {
      borderColor: '#3E8FFA',
    },
  },
  disabledCard: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  cardContent: {
    padding: '24px',
  },
  cardInnerBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  imageBox: {
    flexShrink: 0,
  },
  image: {
    width: '129px',
    height: '90px',
    objectFit: 'contain',
    borderRadius: '8px',
  },
  textBox: {
    flex: 1,
  },
  cardTitle: {
    color: '#3B3C40',
    marginBottom: '4px',
    fontSize: '20px',
    fontWeight: 500,
    lineHeight: '28px',
  },
  cardDescription: {
    color: '#3B3C40',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '22px',
  },
  selectedCount: {
    color: '#3E8FFA',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '22px',
    marginTop: '4px',
  },
  tooltipLink: {
    color: '#D8EFFE',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  tooltipContent: {
    fontFamily: 'Noto Sans',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '-0.24px',
  },
  tooltip: {
    padding: '6px 0px 6px 12px',
    minWidth: '380px',
    backgroundColor: '#232426',
  },
};

const imageStyles: React.CSSProperties = {
  width: '129px',
  height: '90px',
  objectFit: 'contain',
  borderRadius: '8px',
};

export function SignerTypeCard({
  type,
  title,
  description,
  imageUrl,
  selectedCount = 0,
  onClick,
  disabled = false,
}: SignerTypeCardProps) {
  const { checkFlag } = useFeatureFlags();
  const lang = useLanguage();
  const { lang: currentLang } = useUserDataStore();
  const isDynamicSignatureEnabled = checkFlag('DynamicSignature');

  const handleCardClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.stopPropagation();
      return;
    }
    onClick();
  };

  const handleTooltipLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const langCode = currentLang === 'es' ? 'es' : currentLang === 'pt' ? 'pt' : 'en';
    window.open(`https://datascope.io/${langCode}/pricing/`, '_blank');
  };

  const getTooltipContent = () => {
    return (
      <Box sx={customStyles.tooltipContent}>
        {lang.formSignatureBuilder.dynamicSignerTooltip
          .split(lang.formSignatureBuilder.upgradePlan)
          .map((part: string, index: number, array: string[]) => (
            <span key={index}>
              {part}
              {index < array.length - 1 && (
                <Box
                  component='span'
                  sx={customStyles.tooltipLink}
                  onClick={handleTooltipLinkClick}
                >
                  {lang.formSignatureBuilder.upgradePlan}
                </Box>
              )}
            </span>
          ))}
      </Box>
    );
  };

  const shouldShowTooltip = type === SignerType.DYNAMIC && !isDynamicSignatureEnabled;

  const cardElement = (
    <Card
      sx={[customStyles.signerCard, disabled && customStyles.disabledCard] as SxProps<Theme>}
      onClick={handleCardClick}
    >
      <CardContent sx={customStyles.cardContent}>
        <Box sx={customStyles.cardInnerBox}>
          <Box sx={customStyles.imageBox}>
            <img
              src={imageUrl}
              alt=''
              style={imageStyles}
            />
          </Box>

          <Box sx={customStyles.textBox}>
            <Typography sx={customStyles.cardTitle}>{title}</Typography>
            <Typography sx={customStyles.cardDescription}>{description}</Typography>
            {selectedCount > 0 && (
              <Typography sx={customStyles.selectedCount}>
                {selectedCount}{' '}
                {selectedCount === 1
                  ? lang.formSignatureBuilder.selectedSignerSingular
                  : lang.formSignatureBuilder.selectedSignersPlural}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return shouldShowTooltip ? (
    <Tooltip
      title={getTooltipContent()}
      placement='bottom'
      arrow
      slotProps={{
        tooltip: {
          sx: customStyles.tooltip,
        },
      }}
    >
      <Box>{cardElement}</Box>
    </Tooltip>
  ) : (
    cardElement
  );
}

export default SignerTypeCard;
